import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * External link with an image preview tooltip that appears on hover.
 *
 * The tooltip is rendered via a portal into <body> with `position: fixed`
 * anchored to the link's viewport rect. This avoids two problems:
 *  1) parent containers with `overflow: hidden` / `overflow: auto` clipping it,
 *  2) reflow-induced hover flicker (tooltip appearing → parent grows → mouse
 *     leaves anchor → tooltip disappears → loop) that happened when the table
 *     had only one row.
 */
const LinkWithPreview = ({ href, preview, title, testId, children }) => {
  const anchorRef = useRef(null);
  const [hover, setHover] = useState(false);
  const [imgOk, setImgOk] = useState(true);
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!hover) return;
    const update = () => {
      if (anchorRef.current) setRect(anchorRef.current.getBoundingClientRect());
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [hover]);

  if (!href) return <span className="text-slate-300">—</span>;

  const showImage = hover && preview && imgOk;
  const showFallback = hover && !preview;

  // Tooltip dimensions
  const W = showImage ? 240 : 200;
  const H = showImage ? 160 : 32;

  // Compute fixed-position placement anchored to the link, staying in viewport.
  let tooltipStyle = null;
  if (rect) {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Prefer below the link; if not enough room, place above.
    let top = rect.bottom + 6;
    if (top + H > vh - 8) top = Math.max(8, rect.top - H - 6);
    // Prefer aligning right edge to the link (icon usually sits near right side).
    let left = rect.right - W;
    if (left < 8) left = 8;
    if (left + W > vw - 8) left = vw - W - 8;
    tooltipStyle = { position: "fixed", top, left, width: W, height: showImage ? H : undefined, zIndex: 10000 };
  }

  return (
    <>
      <a
        ref={anchorRef}
        href={href}
        target="_blank"
        rel="noreferrer"
        data-testid={testId}
        className="text-[#007AFF] inline-flex items-center hover:underline"
        title={title || "Apri annuncio"}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onFocus={() => setHover(true)}
        onBlur={() => setHover(false)}
      >
        {children}
      </a>
      {tooltipStyle && showImage && createPortal(
        <div
          className="pointer-events-none rounded-xl overflow-hidden border border-slate-200 bg-white shadow-2xl"
          style={tooltipStyle}
          data-testid="link-preview-portal"
        >
          <img
            src={preview}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover"
            onError={() => setImgOk(false)}
          />
        </div>,
        document.body
      )}
      {tooltipStyle && showFallback && createPortal(
        <div
          className="pointer-events-none rounded-lg bg-slate-900 text-white text-[10px] uppercase tracking-widest px-2 py-1 shadow-lg whitespace-nowrap"
          style={{ ...tooltipStyle, height: undefined }}
          data-testid="link-preview-portal-fallback"
        >
          Anteprima non disponibile
        </div>,
        document.body
      )}
    </>
  );
};

export default LinkWithPreview;
